/**
 * Assign Shifts (manager) / My Shift (developer).
 *
 * Manager: weekly burn hero, burn simulator, shift configuration and a
 * 7-day interactive calendar planner with per-day coverage.
 * Developer: personal schedule, earnings gauge and weekly agenda.
 * Self-contained styling (dashboard-pages.css).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  ArrowRight,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronUp,
  Clock3,
  Flame,
  RotateCcw,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { useTeamDashboard } from '../TeamDashboardContext';
import './dashboard-pages.css';

const T = {
  accent: '#6e6bff',
  accent2: '#a855f7',
  cyan: '#22d3ee',
  green: '#34d399',
  amber: '#fbbf24',
  red: '#fb4d6d',
};

const FONT_ID = 'tdp-fonts';
function ensureFonts() {
  if (document.getElementById(FONT_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_ID;
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const SHIFT_PRESETS = ['09:00 - 17:00', '10:00 - 18:00', '11:00 - 19:00', '12:00 - 20:00', 'Off'];

interface ShiftMember {
  initials: string;
  name: string;
  role: string;
  color: string;
  rate: number;
  shift: string;
  days: boolean[];
  dayShifts?: string[];
}

const DEMO_MEMBERS: ShiftMember[] = [
  { initials: 'AK', name: 'Arjun Kumar', role: 'owner', color: T.accent, rate: 500, shift: '09:00 - 17:00', days: [true, true, true, true, true, false, false] },
  { initials: 'SR', name: 'Sneha Rao', role: 'lead', color: T.green, rate: 400, shift: '10:00 - 18:00', days: [true, true, true, true, false, false, false] },
  { initials: 'RM', name: 'Rahul Mehta', role: 'dev', color: T.red, rate: 300, shift: '11:00 - 19:00', days: [false, true, true, true, true, false, false] },
  { initials: 'PK', name: 'Priya Kapoor', role: 'dev', color: T.amber, rate: 500, shift: '09:00 - 17:00', days: [true, true, false, true, true, false, false] },
];

const Card: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({ children, className = '', delay = 0 }) => (
  <motion.section
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className={`tdp-card ${className}`}
  >
    {children}
  </motion.section>
);

const SectionTitle: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({ children, right }) => (
  <div className="tdp-section-title">
    <span>{children}</span>
    {right}
  </div>
);

function Gauge({ pct, size = 108 }: { pct: number; size?: number }) {
  const stroke = 9;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const [shown, setShown] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / 1100);
      setShown(pct * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [pct]);
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--tdp-border)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={T.green}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - shown / 100)}
          style={{ filter: `drop-shadow(0 0 6px ${T.green}66)` }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span className="tdp-mono" style={{ fontSize: 19, fontWeight: 700, color: 'var(--tdp-text)' }}>
          {Math.round(shown)}%
        </span>
        <span className="tdp-mono" style={{ fontSize: 7.5, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--tdp-faint)' }}>
          cap reached
        </span>
      </div>
    </div>
  );
}

export default function WcShiftsPage() {
  const { showToast, isReal, isManager, members: ctxMembers, currentMemberName, currentMemberInitials, theme } = useTeamDashboard();

  const [viewMode, setViewMode] = useState<'cost' | 'hours'>('cost');
  const [showSimulator, setShowSimulator] = useState(false);
  const [simRate, setSimRate] = useState(500);
  const [simHours, setSimHours] = useState(8);
  const [simDays, setSimDays] = useState(5);

  const sourceMembers: ShiftMember[] = useMemo(() => {
    if (isReal && ctxMembers.length > 0) {
      return ctxMembers.map((m, i) => ({
        initials: m.initials,
        name: m.name,
        role: m.role,
        color: [T.accent, T.green, T.red, T.amber][i % 4],
        rate: m.rate,
        shift: m.shiftStart && m.shiftEnd ? `${m.shiftStart} - ${m.shiftEnd}` : '09:00 - 17:00',
        days: [true, true, true, true, true, false, false],
        dayShifts: undefined,
      }));
    }
    return DEMO_MEMBERS;
  }, [isReal, ctxMembers]);

  const [members, setMembers] = useState<ShiftMember[]>(sourceMembers);
  useEffect(() => {
    setMembers(sourceMembers);
  }, [sourceMembers]);

  useEffect(() => {
    ensureFonts();
  }, []);

  /* ── derived ── */
  const weeklyBurn = useMemo(
    () => members.reduce((sum, m) => sum + m.rate * 8 * m.days.filter(Boolean).length, 0),
    [members],
  );
  const weeklyHours = useMemo(() => members.reduce((sum, m) => sum + 8 * m.days.filter(Boolean).length, 0), [members]);
  const simTotal = simRate * simHours * simDays * Math.max(members.length, 1);
  const coverage = useMemo(() => DAYS.map((_, dayIdx) => members.filter((m) => m.days[dayIdx]).length), [members]);

  /* ── mutations ── */
  function updateShift(initials: string, shift: string) {
    setMembers((prev) =>
      prev.map((m) => {
        if (m.initials !== initials) return m;
        const off = shift.toLowerCase() === 'off';
        return {
          ...m,
          shift,
          days: off ? m.days.map(() => false) : [true, true, true, true, true, false, false],
          dayShifts: m.days.map((active) => (active && !off ? shift : 'Off')),
        };
      }),
    );
  }

  function toggleCell(initials: string, dayIdx: number) {
    setMembers((prev) =>
      prev.map((m) => {
        if (m.initials !== initials) return m;
        const nextDays = [...m.days];
        nextDays[dayIdx] = !nextDays[dayIdx];
        const nextDayShifts = m.dayShifts ? [...m.dayShifts] : m.days.map((a) => (a ? m.shift : 'Off'));
        nextDayShifts[dayIdx] = nextDays[dayIdx] ? m.shift : 'Off';
        return { ...m, days: nextDays, dayShifts: nextDayShifts };
      }),
    );
  }

  function cycleCellShift(initials: string, dayIdx: number) {
    setMembers((prev) =>
      prev.map((m) => {
        if (m.initials !== initials) return m;
        const nextDayShifts = m.dayShifts ? [...m.dayShifts] : m.days.map((a) => (a ? m.shift : 'Off'));
        const current = nextDayShifts[dayIdx] || 'Off';
        const next = SHIFT_PRESETS[(SHIFT_PRESETS.indexOf(current) + 1) % SHIFT_PRESETS.length];
        nextDayShifts[dayIdx] = next;
        const nextDays = [...m.days];
        nextDays[dayIdx] = next.toLowerCase() !== 'off';
        return { ...m, days: nextDays, dayShifts: nextDayShifts, shift: m.shift };
      }),
    );
  }

  function resetStandard() {
    setMembers((prev) =>
      prev.map((m) => ({
        ...m,
        shift: '09:00 - 17:00',
        days: [true, true, true, true, true, false, false],
        dayShifts: ['09:00 - 17:00', '09:00 - 17:00', '09:00 - 17:00', '09:00 - 17:00', '09:00 - 17:00', 'Off', 'Off'],
      })),
    );
    showToast('Shifts reset', 'Standard 09:00 – 17:00, Monday to Friday applied to everyone.', 'success');
  }

  function assignShift(initials: string) {
    const member = members.find((m) => m.initials === initials);
    if (!member) return;
    const activeDays = member.days.filter(Boolean).length;
    showToast('Shift assigned', `${member.name} scheduled for ${activeDays} days · ${member.shift}`, 'success');
  }

  const isDark = theme === 'dark';

  /* ═══════════ DEVELOPER — MY SHIFT ═══════════ */
  if (!isManager) {
    const me = members.find((m) => m.initials === currentMemberInitials) ?? members[0];
    const myDays = me?.days ?? [true, true, true, true, true, false, false];
    const myShift = me?.shift ?? '09:00 - 17:00';
    const myRate = me?.rate ?? 500;
    const activeDays = myDays.filter(Boolean).length;
    const estEarnings = myRate * 8 * activeDays;
    const weeklyCap = 25000;
    const capPct = Math.min(100, Math.round((estEarnings / weeklyCap) * 100));

    return (
      <div data-theme={isDark ? 'dark' : 'light'} className="tdp" style={{ minHeight: '100%' }}>
        <div className="tdp-wrap">
          <div className="tdp-hero">
            <div className="tdp-hero-row">
              <div className="tdp-hero-main">
                <div className="tdp-eyebrow">
                  <CalendarDays size={11} />
                  Work clock · my shift
                </div>
                <h1 className="tdp-title">My Shift</h1>
                <p className="tdp-sub">Your schedule, projected earnings and weekly agenda — managed by your team lead.</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="tdp-burn-val">₹{estEarnings.toLocaleString()}</div>
                <div className="tdp-burn-label">Projected weekly earnings</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 18 }} className="tdp-shift-grid">
            <style>{`@media (min-width: 1080px) { .tdp-shift-grid { grid-template-columns: minmax(0,1.1fr) minmax(0,1fr) !important; } }`}</style>

            <Card delay={0.08} className="tdp-gauge-wrap">
              <Gauge pct={capPct} />
              <div style={{ flex: 1 }}>
                <div className="tdp-kpi-label">Earnings gauge & rate details</div>
                <div className="tdp-mono" style={{ fontSize: 24, fontWeight: 700, color: 'var(--tdp-accent)', marginTop: 6 }}>
                  ₹{estEarnings.toLocaleString()}
                </div>
                <div className="tdp-feed-meta" style={{ marginTop: 2 }}>estimated shift earnings</div>
                <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
                  {[
                    { label: 'Rate', value: `₹${myRate}/hr` },
                    { label: 'Shift window', value: myShift },
                    { label: 'Active days', value: `${activeDays} / week` },
                    { label: 'Weekly shift cap', value: `₹${weeklyCap.toLocaleString()} / week` },
                  ].map((row) => (
                    <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--tdp-dim)' }}>
                      <span>{row.label}</span>
                      <b className="tdp-mono" style={{ color: 'var(--tdp-text)' }}>{row.value}</b>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            <Card delay={0.12}>
              <div className="tdp-section-title" style={{ padding: '14px 16px 10px', margin: 0, borderBottom: '1px solid var(--tdp-border)' }}>
                <span>Weekly schedule agenda</span>
                <Clock3 size={11} style={{ color: 'var(--tdp-faint)' }} />
              </div>
              {DAYS.map((day, i) => {
                const active = myDays[i];
                const shift = me?.dayShifts?.[i] ?? (active ? myShift : 'Off');
                return (
                  <div key={day} className="tdp-agenda-row">
                    <span className="tdp-agenda-day">{day}</span>
                    <span className="tdp-agenda-dot" style={{ background: active ? T.green : 'var(--tdp-border)' }} />
                    <span style={{ flex: 1, color: active ? 'var(--tdp-text)' : 'var(--tdp-faint)', fontWeight: active ? 600 : 400 }}>
                      {active ? `Active shift · ${shift}` : 'Rest day'}
                    </span>
                    <span className="tdp-pill" style={{ background: active ? `${T.green}1a` : 'var(--tdp-hover)', color: active ? T.green : 'var(--tdp-faint)' }}>
                      {active ? 'active' : 'off'}
                    </span>
                  </div>
                );
              })}
            </Card>
          </div>

          <div className="tdp-section" style={{ marginTop: 18 }}>
            <SectionTitle
              right={
                <button className="tdp-section-link" onClick={() => setShowSimulator(!showSimulator)}>
                  {showSimulator ? 'Hide' : 'Open'} burn simulator {showSimulator ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                </button>
              }
            >
              Earnings simulator
            </SectionTitle>
            <AnimatePresence>
              {showSimulator && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ overflow: 'hidden' }}>
                  <Card>
                    <div className="tdp-sim-grid">
                      <div>
                        {[
                          { label: 'Hourly rate', val: simRate, set: setSimRate, min: 100, max: 1500, step: 50, fmt: `₹${simRate}/hr` },
                          { label: 'Hours per day', val: simHours, set: setSimHours, min: 1, max: 12, step: 1, fmt: `${simHours}h` },
                          { label: 'Active days', val: simDays, set: setSimDays, min: 1, max: 7, step: 1, fmt: `${simDays} days` },
                        ].map((s) => (
                          <div key={s.label} className="tdp-slider-row">
                            <div className="tdp-slider-head">
                              <span>{s.label}</span>
                              <b>{s.fmt}</b>
                            </div>
                            <input type="range" className="tdp-slider" min={s.min} max={s.max} step={s.step} value={s.val} onChange={(e) => s.set(Number(e.target.value))} />
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 14, border: '1px solid var(--tdp-border)', background: 'var(--tdp-hover)', padding: 18 }}>
                        <div className="tdp-kpi-label">Simulated weekly earnings</div>
                        <div className="tdp-mono" style={{ fontSize: 28, fontWeight: 700, color: T.green, marginTop: 6 }}>
                          ₹{(simRate * simHours * simDays).toLocaleString()}
                        </div>
                        <div className="tdp-feed-meta" style={{ marginTop: 4 }}>
                          ₹{simRate} × {simHours}h × {simDays} days
                        </div>
                        {simRate * simHours * simDays > estEarnings && (
                          <span className="tdp-pill" style={{ marginTop: 10, background: `${T.cyan}1a`, color: T.cyan }}>
                            <TrendingUp size={9} /> +₹{(simRate * simHours * simDays - estEarnings).toLocaleString()} vs current
                          </span>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    );
  }

  /* ═══════════ MANAGER — ASSIGN SHIFTS ═══════════ */
  return (
    <div data-theme={isDark ? 'dark' : 'light'} className="tdp" style={{ minHeight: '100%' }}>
      <div className="tdp-wrap">
        <div className="tdp-hero">
          <div className="tdp-burn-hero">
            <div className="tdp-hero-main">
              <div className="tdp-eyebrow">
                <CalendarDays size={11} />
                Work clock · assign shifts
              </div>
              <h1 className="tdp-title">Assign Shifts</h1>
              <p className="tdp-sub">Manage developer schedules and track projected operational burn — 8h per shift day.</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
              <div style={{ textAlign: 'right' }}>
                <div className="tdp-burn-val">{viewMode === 'cost' ? `₹${weeklyBurn.toLocaleString()}` : `${weeklyHours}h`}</div>
                <div className="tdp-burn-label">Projected weekly {viewMode === 'cost' ? 'burn' : 'hours'}</div>
              </div>
              <div className="tdp-toggle">
                <button className={viewMode === 'cost' ? 'tdp-on' : ''} onClick={() => setViewMode('cost')}>
                  Cost view
                </button>
                <button className={viewMode === 'hours' ? 'tdp-on' : ''} onClick={() => setViewMode('hours')}>
                  Hours view
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* simulator */}
        <div className="tdp-section">
          <Card delay={0.06}>
            <button className="tdp-expand" onClick={() => setShowSimulator(!showSimulator)}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Flame size={13} style={{ color: T.amber }} />
                Open Burn Simulator & Forecasts
              </span>
              {showSimulator ? <ChevronUp size={13} style={{ color: 'var(--tdp-faint)' }} /> : <ChevronDown size={13} style={{ color: 'var(--tdp-faint)' }} />}
            </button>
            <AnimatePresence>
              {showSimulator && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ overflow: 'hidden' }}>
                  <div className="tdp-sim-grid" style={{ borderTop: '1px solid var(--tdp-border)' }}>
                    <div>
                      {[
                        { label: 'Avg hourly rate', val: simRate, set: setSimRate, min: 100, max: 1500, step: 50, fmt: `₹${simRate}/hr` },
                        { label: 'Hours per shift', val: simHours, set: setSimHours, min: 1, max: 12, step: 1, fmt: `${simHours}h` },
                        { label: 'Shift days / week', val: simDays, set: setSimDays, min: 1, max: 7, step: 1, fmt: `${simDays} days` },
                      ].map((s) => (
                        <div key={s.label} className="tdp-slider-row">
                          <div className="tdp-slider-head">
                            <span>{s.label}</span>
                            <b>{s.fmt}</b>
                          </div>
                          <input type="range" className="tdp-slider" min={s.min} max={s.max} step={s.step} value={s.val} onChange={(e) => s.set(Number(e.target.value))} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 14, border: '1px solid var(--tdp-border)', background: 'var(--tdp-hover)', padding: 18 }}>
                      <div className="tdp-kpi-label">Simulated team burn</div>
                      <div className="tdp-mono" style={{ fontSize: 28, fontWeight: 700, color: simTotal > weeklyBurn ? T.red : T.green, marginTop: 6 }}>
                        ₹{simTotal.toLocaleString()}
                      </div>
                      <div className="tdp-feed-meta" style={{ marginTop: 4 }}>
                        ₹{simRate} × {simHours}h × {simDays}d × {Math.max(members.length, 1)} members
                      </div>
                      <span className="tdp-pill" style={{ marginTop: 10, background: simTotal > weeklyBurn ? `${T.red}1a` : `${T.green}1a`, color: simTotal > weeklyBurn ? T.red : T.green }}>
                        {simTotal > weeklyBurn ? `+₹${(simTotal - weeklyBurn).toLocaleString()} over current` : `₹${(weeklyBurn - simTotal).toLocaleString()} under current`}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </div>

        {/* shift configuration */}
        <div className="tdp-section">
          <SectionTitle
            right={
              <button className="tdp-section-link" onClick={resetStandard}>
                <RotateCcw size={9} /> Reset standard shifts
              </button>
            }
          >
            Shifts configuration
          </SectionTitle>
          <Card delay={0.1}>
            {members.map((m) => {
              const activeDays = m.days.filter(Boolean).length;
              return (
                <div key={m.initials} className="tdp-shift-row">
                  <span className="tdp-avatar" style={{ width: 32, height: 32, fontSize: 10, background: `linear-gradient(135deg, ${m.color}, ${T.accent2})` }}>
                    {m.initials}
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="tdp-shift-name">{m.name}</div>
                    <div className="tdp-shift-meta">
                      {m.role} · ₹{m.rate}/hr · <span className="tdp-shift-est">₹{(m.rate * 8 * activeDays).toLocaleString()} est.</span>
                    </div>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <select className="tdp-select" style={{ width: 'auto', paddingRight: 30, appearance: 'none' }} value={m.shift} onChange={(e) => updateShift(m.initials, e.target.value)}>
                      {SHIFT_PRESETS.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={12} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--tdp-faint)', pointerEvents: 'none' }} />
                  </div>
                  <button className="tdp-btn tdp-btn-ghost tdp-btn-sm" onClick={() => assignShift(m.initials)}>
                    <Check size={11} /> Assign
                  </button>
                </div>
              );
            })}
          </Card>
        </div>

        {/* 7-day calendar */}
        <div className="tdp-section">
          <SectionTitle
            right={
              <span className="tdp-mono" style={{ fontSize: 9, color: 'var(--tdp-faint)' }}>
                click cell to toggle · double-click to cycle shift
              </span>
            }
          >
            7-day weekly planner
          </SectionTitle>
          <Card delay={0.14} className="tdp-cal">
            <div className="tdp-cal-grid">
              <div className="tdp-cal-head" style={{ textAlign: 'left', paddingLeft: 10 }}>
                Team member
              </div>
              {DAYS.map((d, i) => (
                <div key={d} className="tdp-cal-head" style={{ color: coverage[i] > 0 ? 'var(--tdp-accent)' : undefined }}>
                  {d}
                </div>
              ))}

              {members.map((m) => (
                <React.Fragment key={m.initials}>
                  <div className="tdp-cal-member">
                    <span className="tdp-avatar" style={{ width: 26, height: 26, fontSize: 8.5, background: `linear-gradient(135deg, ${m.color}, ${T.accent2})` }}>
                      {m.initials}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--tdp-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {m.name}
                      </div>
                      <div className="tdp-feed-meta">₹{(m.rate * 8 * m.days.filter(Boolean).length).toLocaleString()}</div>
                    </div>
                  </div>
                  {DAYS.map((_, dayIdx) => {
                    const active = m.days[dayIdx];
                    const shift = m.dayShifts?.[dayIdx] ?? (active ? m.shift : 'Off');
                    return (
                      <button
                        key={dayIdx}
                        className={`tdp-cal-cell ${active ? 'tdp-cal-cell-on' : 'tdp-cal-cell-off'}`}
                        onClick={() => toggleCell(m.initials, dayIdx)}
                        onDoubleClick={() => cycleCellShift(m.initials, dayIdx)}
                        title={active ? `${m.name} · ${shift} (click: off, double-click: change)` : `${m.name} · off (click: activate)`}
                      >
                        <span className="tdp-cal-cell-type">{active ? 'DAY' : 'OFF'}</span>
                        {active && <span className="tdp-cal-cell-time">{shift.split(' ')[0]}</span>}
                      </button>
                    );
                  })}
                </React.Fragment>
              ))}

              {/* coverage footer */}
              <div className="tdp-cal-head" style={{ textAlign: 'left', paddingLeft: 10 }}>
                Coverage
              </div>
              {DAYS.map((_, i) => (
                <div key={i} className="tdp-cal-cover" style={{ color: coverage[i] === 0 ? T.red : 'var(--tdp-dim)' }}>
                  {coverage[i] === 0 ? 'gap' : `${coverage[i]}/${members.length}`}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
